import { useRef, useState, useEffect, useContext, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { useReactFlow } from "reactflow";
import { Formik, Form } from "formik";
import FormField from "layouts/Flow Designer/components/FormField";
import Box from "@mui/material/Box";
import DiscardChangesDialog from "layouts/Flow Designer/components/DiscardChangesDialog";
import validations from "./schemas/validations";
import { form } from "./schemas/form";
import initialValues from "./schemas/initialValues";
import FormTabs from "../FormTabs";
import {
  closeTriggerPropertiesPanel,
  updateFlowTrigger,
  clearSelectedTrigger,
  setSelectedTrigger
} from "redux/actions/flowDesigner/triggersActionCreator";
import { openDiscardDialog } from "redux/actions/flowDesigner/commonActionCreator";
import { COMMON_COMPONENT_TYPES, NODE_TYPES, TRIGGER_TYPES } from "constants/flowDesigner";
import TriggerFormWrapper from "../TriggerFormWrapper";
import { updateContextTrigger } from "redux/actions/flowDesigner/editorContextActionCreator";
import { TabContextApi } from "../../../../context";
import { credentialManagementData } from "layouts/pages/credential-management/schemas";
import { toast } from "react-toastify";
import { FormFieldsContext } from "layouts/Flow Designer/context";
import AdditionalOptions from "./AdditionalOptions";
import { TRIGGER_RESPONSE_CODES } from "constants/flowDesigner";
import Comments from "layouts/Flow Designer/components/FlowPropertiesPanel/components/Comments";
import { redisTriggerSubscribe } from "api/ExecutorServiceApi";
import SoftBox from "components/SoftBox";
import CredentialManagementAction from "layouts/pages/credential-management/components/CredentialManagementAction";
import CredentialDialogForm from "layouts/pages/credential-management/components/CredentialDialogForm";
import { getEnv } from "helpers/environment";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { Actions } from "flexlayout-react";
import { FLEX_LAYOUT_COMPONENT } from "constants/flowDesigner";
import { showStartNodePanel } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import {
  createResourceDependency,
  deleteResourceDependency,
  updateResourceDependency
} from "redux/actions/portal/resourceDependenciesActionCreator";
import { getResourceDependencyContextId } from "helpers/resourceDependencies";

const TabPanel = ({ children, value = 0, index = 0 }) => {
  return <>{value === index && children}</>;
};

const HttpEndpointTriggerForm = ({
  selectedTrigger,
  showTriggerPropertiesPanel,
  allowEditWorkflow
}) => {
  const { user } = useAuth0();
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const reactFlowInstance = useReactFlow();
  const nodes = reactFlowInstance.getNodes();
  const { credentials } = useSelector((store) => store?.portal?.credentialManagement);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);
  const [tabValue, setTabValue] = useState(0);
  const { autoSaveFlow } = useContext(TabContextApi);
  const dispatch = useDispatch();
  const { flowTriggers } = useSelector((store) => store?.designer?.triggers);
  const selectedTriggerProps = flowTriggers.find((item) => item.type === selectedTrigger?.type)?.properties;
  const targetUserId = user?.sub?.replace(/\|/g, "_");
  const allowedTypes =
    selectedTrigger?.type === TRIGGER_TYPES.REDIS ? ["redis"] : ["bearerToken", "basicAuth"];
  const credentialApplicationTypeOptions = [
    ...credentialManagementData
      .filter((cred) => {
        return allowedTypes.includes(cred.type);
      })
      .sort((a, b) => a.type.localeCompare(b.type))
      .map((data) => ({
        value: data.type,
        label: data.name
      }))
  ];
  const { model } = useFlexLayout();

  const isFlowActive = currentSavedFlow?.shared_status === "active";
  const triggerType =
    selectedTrigger?.type === TRIGGER_TYPES.WEBHOOK
      ? "webhook"
      : selectedTrigger?.type === TRIGGER_TYPES.REDIS
        ? "redisendpoint/subscribe"
        : "restendpoint";

  initialValues.endpointUrl =
    getEnv("EXECUTOR_API_SOCKET") +
    `/${triggerType}?tenant_id=${user?.stripeId}&flow_id=${currentSavedFlow?.id}${
      isFlowActive === false ? "&draft=true" : ""
    }`;
  initialValues.method = selectedTriggerProps?.method
    ? selectedTriggerProps?.method
    : selectedTrigger?.type === TRIGGER_TYPES.WEBHOOK
      ? ["POST"]
      : ["GET"];
  initialValues.required =
    selectedTriggerProps?.credentials?.required ||
    selectedTrigger.type === TRIGGER_TYPES.REDIS ||
    false;
  initialValues.respond = selectedTriggerProps?.respond?.value || "last_node_finishes";
  initialValues.node = selectedTriggerProps?.respond?.node || "";
  initialValues.notes = selectedTriggerProps?.notes || "";
  initialValues.additionalOptions = selectedTriggerProps?.additionalOptions || [];
  const savedCredentialType = selectedTriggerProps?.credentials?.credentialType || "";
  const selectedCredentialType =
    selectedTrigger.type === TRIGGER_TYPES.REDIS
      ? "redis"
      : allowedTypes.includes(savedCredentialType)
        ? savedCredentialType
        : "";

  initialValues.credentialType = selectedCredentialType;
  initialValues.credential = selectedCredentialType
    ? selectedTriggerProps?.credentials?.value || ""
    : "";
  initialValues.payloadContentType = selectedTriggerProps?.payloadContentType || "json";
  initialValues.formData = selectedTriggerProps?.formData || [];
  initialValues.payload = selectedTriggerProps?.payload || "";
  initialValues.responseCode = selectedTriggerProps?.responseCode || "";
  initialValues.customResponseCode = selectedTriggerProps?.customResponseCode || "";
  initialValues.responseHeaders = selectedTriggerProps?.responseHeaders || [];
  initialValues.allowedOrigins = selectedTriggerProps?.allowedOrigins || "";
  initialValues.returnConsoleLog = selectedTriggerProps?.returnConsoleLog || false;
  initialValues.topics = selectedTriggerProps?.topics || [];

  const {
    formId,
    formField: {
      endpointUrl,
      method,
      credentialType,
      credential,
      required,
      respond,
      node,
      notes,
      returnConsoleLog,
      topics
    }
  } = form;

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSaveSubmit = async (values, actions) => {
    const {
      endpointUrl,
      method,
      credentialType,
      required,
      credential,
      respond,
      additionalOptions,
      responseCode,
      customResponseCode,
      responseHeaders,
      payloadContentType,
      formData,
      payload,
      allowedOrigins,
      node,
      notes,
      returnConsoleLog,
      topics
    } = values;

    setSubmitting(true);
    actions.setSubmitting(false);

    const validCredentialType =
      selectedTrigger.type === TRIGGER_TYPES.REDIS || allowedTypes.includes(credentialType)
        ? credentialType
        : "";

    const triggerProps = {
      method: method.map((option) => (typeof option === "object" ? option.value : option)),
      endpoint_url: endpointUrl,
      credentials: {
        required,
        value: required && validCredentialType ? credential : "",
        credentialType: validCredentialType
      },
      additionalOptions,
      payloadContentType: payload ? payloadContentType : "",
      formData,
      payload,
      allowedOrigins,
      responseHeaders,
      responseCode,
      customResponseCode: responseCode === TRIGGER_RESPONSE_CODES.CUSTOM ? customResponseCode : "",
      respond: {
        node,
        value: respond
      },
      notes,
      returnConsoleLog,
      topics
    };

    const foundContentTypeHeader = responseHeaders.find(
      (header) => header.key.toLowerCase() === "content-type"
    );

    if (foundContentTypeHeader) {
      if (
        payloadContentType &&
        !foundContentTypeHeader.value.toLowerCase().includes(payloadContentType)
      ) {
        return toast.error("Content type header does not match payload content type configured");
      }
    } else {
      if (payloadContentType) {
        triggerProps.payloadContentType = payloadContentType;
      }
    }

    if (selectedTrigger.type === TRIGGER_TYPES.REDIS) {
      await subscribeToRedisTopics(triggerProps);
    }

    updateStoreAndSaveFlow(triggerProps);

    const previousCredentialId = selectedTriggerProps?.credentials?.value;
    const dependencyContextId = getResourceDependencyContextId(
      currentSavedFlow?.id,
      selectedTrigger?.type
    );

    const hasOldDepToDelete = previousCredentialId && previousCredentialId !== triggerProps.credentials?.value;
    const hasNewDepToCreate = triggerProps.credentials?.value;

    if (hasOldDepToDelete && hasNewDepToCreate) {
      dispatch(
        updateResourceDependency({
          tenantId: currentSavedFlow.tenant_id,
          oldInput: {
            sourceId: currentSavedFlow.id,
            targetId: previousCredentialId,
            sourceResourceType: "flow",
            targetResourceType: "credential",
            contextId: dependencyContextId
          },
          newInput: {
            sourceId: currentSavedFlow.id,
            targetId: triggerProps.credentials.value,
            sourceResourceType: "flow",
            targetResourceType: "credential",
            contextId: dependencyContextId
          }
        })
      );
    } else if (hasOldDepToDelete) {
      dispatch(
        deleteResourceDependency({
          tenantId: currentSavedFlow.tenant_id,
          sourceId: currentSavedFlow.id,
          targetId: previousCredentialId,
          sourceResourceType: "flow",
          targetResourceType: "credential",
          contextId: dependencyContextId
        })
      );
    } else if (hasNewDepToCreate && previousCredentialId !== triggerProps.credentials.value) {
      dispatch(
        createResourceDependency({
          tenantId: currentSavedFlow.tenant_id,
          sourceId: currentSavedFlow.id,
          targetId: triggerProps.credentials.value,
          sourceResourceType: "flow",
          targetResourceType: "credential",
          contextId: dependencyContextId
        })
      );
    }

    setSubmitting(false);
    // closeForm();
  };

  const subscribeToRedisTopics = async (triggerProps) => {
    try {
      const redisTriggerSubscribeResponse = await redisTriggerSubscribe(
        currentSavedFlow?.tenant_id,
        currentSavedFlow?.id,
        triggerProps?.topics,
        triggerProps?.credentials?.value
      );

      if (redisTriggerSubscribeResponse?.isSuccessful) {
        toast.success(redisTriggerSubscribeResponse?.message);
      } else {
        toast.error(redisTriggerSubscribeResponse?.message);
      }
    } catch (error) {
      console.error("subcribeToRedisTopics error: ", error);
    }
  };

  const updateStoreAndSaveFlow = (triggerProps) => {
    const currentTrigger = { ...selectedTrigger };
    currentTrigger.properties = { ...currentTrigger.properties, ...triggerProps };
    dispatch(updateContextTrigger(currentTrigger.label, currentSavedFlow?.name, triggerProps));
    dispatch(updateFlowTrigger(currentTrigger));
    dispatch(clearSelectedTrigger());
    autoSaveFlow(reactFlowInstance);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    formRef.current.submitForm();
  };

  const handleOpenDiscardDialog = () => {
    dispatch(openDiscardDialog());
  };

  const checkDirtyAndClose = () => {
    if (formRef.current.dirty) {
      handleOpenDiscardDialog();
      return;
    }
    closeForm();
  };

  const closeForm = () => {
    const tabId = "startNodeProps";
    model.doAction(
      Actions.updateNodeAttributes(tabId, {
        component: FLEX_LAYOUT_COMPONENT.startNode,
        config: {}
      })
    );
    model.doAction(Actions.updateNodeAttributes(tabId, { name: "Start Node" }));
    model.doAction(Actions.selectTab(tabId));

    dispatch(setSelectedTrigger(null));
    dispatch(closeTriggerPropertiesPanel());
    dispatch(showStartNodePanel());
  };

  const resetForm = () => {
    formRef.current?.resetForm();
  };

  const generateRespondOptions = (respond) => {
    switch (respond) {
      case "after_specific_node":
        return nodes
          .filter(
            ({ type }) =>
              type !== NODE_TYPES.STATIC && type !== NODE_TYPES.LABEL && type !== NODE_TYPES.HIDDEN
          )
          .map(({ id, data }) => ({
            label: data.customLabel || data.label,
            subLabel: data.componentKey,
            value: id
          }))
          .sort((first, second) => (first.label || "").localeCompare(second.label || ""));
      case "use_code_node":
        return nodes
          .filter((node) => node.data.type === COMMON_COMPONENT_TYPES.CODE)
          .map(({ id, data }) => ({
            label: data.customLabel || data.label,
            subLabel: data.componentKey,
            value: id
          }))
          .sort((first, second) => (first.label || "").localeCompare(second.label || ""));
      default:
        return [];
    }
  };

  const filterRespondOptions = () => {
    switch (selectedTrigger.type) {
      case TRIGGER_TYPES.REST_ENDPOINT:
      case TRIGGER_TYPES.WEBHOOK:
        return respond.options.filter((option) => option.value !== "use_code_node").sort((first, second) => (first.label || "").localeCompare(second.label || ""));
    }

    return respond.options.sort((first, second) => (first.label || "").localeCompare(second.label || ""));
  };

  const createCredentialOptions = (applicationType) => {
    const mappedCredentials = credentials
      .filter((cr) => allowedTypes.includes(cr.application?.applicationType))
      .map((credential) => ({
        label: credential.application?.name,
        value: credential.id,
        credentialsData: credential
      }));
    mappedCredentials.unshift({ label: "None", value: "" });

    if (!applicationType) return [];

    return (
      mappedCredentials
        ?.filter((credential) =>
          applicationType.includes(credential?.credentialsData?.application?.applicationType)
        )
        ?.map((credential) => ({
          label: credential?.credentialsData?.application?.name,
          value: credential?.credentialsData?.id
        }))
        ?.sort((first, second) => (first.label || "").localeCompare(second.label || "")) || []
    );
  };

  useEffect(() => {
    if (showTriggerPropertiesPanel) {
      setTabValue(0);
    } else {
      resetForm();
    }
  }, [showTriggerPropertiesPanel]);

  return (
    <>
      <TriggerFormWrapper
        onSubmit={handleSubmit}
        onCancel={checkDirtyAndClose}
        submitting={submitting}
        tabValue={tabValue}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={handleSaveSubmit}
          validationSchema={validations}
          innerRef={formRef}
          enableReinitialize
        >
          {({ values, errors, touched, setFieldValue }) => {
            const nodeOptions = generateRespondOptions(values.respond);
            const nodeDefaultValue = nodeOptions.find((option) => option.value === values.node);
            const credentialOptions = createCredentialOptions(values.credentialType);

            if (
              credentialOptions?.length &&
              values.credential &&
              !credentialOptions.find((option) => option.value === values.credential)
            ) {
              setFieldValue(credential.name, "");
            }

            return (
              <Form id={formId} autoComplete="off">
                <FormTabs tabValue={tabValue} onChange={handleChangeTab} />
                <Box sx={{ padding: "8px 4px 8px 4px" }}>
                  <TabPanel value={tabValue} index={0}>
                    <FormField
                      type={endpointUrl.type}
                      label={endpointUrl.label}
                      name={endpointUrl.name}
                      disabled={true}
                      readOnly={true}
                      value={values.endpointUrl}
                      multiline
                      rows={3}
                      secondaryCopySuffix={`&targetUserId=${targetUserId}&displayExecutionLogs=true`}
                    />
                    <FormField
                      type={method.type}
                      label={method.label}
                      name={method.name}
                      error={!!errors.method && !!touched.method}
                      placeholder=""
                      options={(selectedTrigger?.type === TRIGGER_TYPES.WEBHOOK
                        ? method.options.filter(
                            ({ value }) => value !== "GET" && value !== "DELETE"
                          )
                        : method.options
                      )?.sortAlphabetical("value")}
                      value={method.options.filter((option) =>
                        values.method.find((valueMethod) =>
                          typeof valueMethod === "string"
                            ? valueMethod === option.value
                            : valueMethod.value === option.value
                        )
                      )}
                      required
                      isDisabled={isFlowActive || !allowEditWorkflow}
                      isHidden={selectedTrigger.type === TRIGGER_TYPES.REDIS}
                    />

                    {!isFlowActive && selectedTrigger.type !== TRIGGER_TYPES.REDIS && (
                      <FormField
                        style={{ marginTop: 20, marginBottom: -10 }}
                        type={required.type}
                        label={required.label}
                        name={required.name}
                        error={errors.required && touched.required}
                        placeholder=""
                        inlineSwitch
                        disabled={isFlowActive || !allowEditWorkflow}
                      />
                    )}
                    {values.required && (
                      <>
                        <SoftBox sx={{ display: "flex", alignItems: "center" }}>
                          <SoftBox sx={{ width: "100%" }}>
                            <FormField
                              type={credentialType.type}
                              label={credentialType.label}
                              name={credentialType.name}
                              error={errors.credentialType && touched.credentialType}
                              placeholder=""
                              required
                              options={credentialApplicationTypeOptions}
                              value={credentialApplicationTypeOptions?.find(
                                (option) => option.value === values.credentialType
                              )}
                              isDisabled={isFlowActive || !allowEditWorkflow}
                            />
                          </SoftBox>
                          <SoftBox
                            sx={{
                              paddingTop: "20px",
                              visibility: isFlowActive || !allowEditWorkflow ? "hidden" : "visible"
                            }}
                          >
                            <CredentialManagementAction
                              credentialData={null}
                              iconOrientation="vertical"
                              isEditor
                            />
                          </SoftBox>
                        </SoftBox>
                        {values.credentialType && (
                          <>
                            <FormField
                              type={credential.type}
                              label={credential.label}
                              name={credential.name}
                              error={errors.credential && touched.credential}
                              placeholder=""
                              required
                              options={credentialOptions}
                              value={
                                credentialOptions?.find(
                                  (option) => option.value === values.credential
                                ) ?? ""
                              }
                              isDisabled={
                                isFlowActive ||
                                selectedTrigger.type === TRIGGER_TYPES.REDIS ||
                                !allowEditWorkflow
                              }
                            />
                          </>
                        )}
                      </>
                    )}
                    {selectedTrigger?.type !== TRIGGER_TYPES.WEBHOOK && (
                      <FormField
                        type={respond.type}
                        label={respond.label}
                        name={respond.name}
                        error={errors.respond && touched.respond}
                        placeholder=""
                        options={filterRespondOptions()}
                        defaultValue={respond.options.find(
                          (option) => option.value === values.respond
                        )}
                        isDisabled={isFlowActive || !allowEditWorkflow}
                        isHidden={selectedTrigger.type === TRIGGER_TYPES.REDIS}
                      />
                    )}
                    {(values.respond === "after_specific_node" ||
                      values.respond === "use_code_node") && (
                      <FormField
                        type={node.type}
                        label={node.label}
                        name={node.name}
                        error={errors.node && touched.node}
                        placeholder=""
                        options={nodeOptions}
                        defaultValue={nodeDefaultValue}
                        isDisabled={isFlowActive || !allowEditWorkflow}
                      />
                    )}
                    {selectedTrigger?.type !== TRIGGER_TYPES.WEBHOOK && (
                      <FormField
                        type={returnConsoleLog.type}
                        name={returnConsoleLog.name}
                        label={returnConsoleLog.label}
                        inlineSwitch
                        disabled={isFlowActive || !allowEditWorkflow}
                      />
                    )}
                    {selectedTrigger.type !== TRIGGER_TYPES.REDIS && (
                      <FormFieldsContext.Provider value={formRef.current || {}}>
                        <AdditionalOptions allowEditWorkflow={allowEditWorkflow} />
                      </FormFieldsContext.Provider>
                    )}
                    {selectedTrigger.type === TRIGGER_TYPES.REDIS && (
                      <FormField
                        type={topics.type}
                        label={topics.label}
                        name={topics.name}
                        placeholder={topics.placeholder}
                        error={true}
                        value={values.topics}
                        required
                        disabled={isFlowActive || !allowEditWorkflow}
                      />
                    )}
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <FormField
                      type={notes.type}
                      label={notes.label}
                      name={notes.name}
                      value={values.notes}
                      multiline
                      rows={5}
                    />
                  </TabPanel>
                </Box>
              </Form>
            );
          }}
        </Formik>
        <DiscardChangesDialog onAccept={closeForm} />
      </TriggerFormWrapper>
      <TabPanel value={tabValue} index={2}>
        <Comments tabValue={tabValue} node_id={selectedTrigger.type} closeForm={closeForm} />
      </TabPanel>
      <CredentialDialogForm />
    </>
  );
};

export default memo(HttpEndpointTriggerForm);
